import React, { useEffect, useRef, useState } from 'react';
import { useSocket } from '../../contexts/SocketContext';
import { formatString } from '@deadpan/shared';
import { useTranslations } from '../../hooks/useTranslations';
import { captureElementImage, downloadImageBlob } from '../../utils/captureElementImage';

export const SimilarMindsResults: React.FC = () => {
  const { socket, gameState, currentRoom, myPlayerId } = useSocket();
  const t = useTranslations();
  const sm = t.games.similarMinds;
  const captureRef = useRef<HTMLDivElement>(null);
  const [isSavingImage, setIsSavingImage] = useState(false);
  const [saveImageError, setSaveImageError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewFilename, setPreviewFilename] = useState('baijia-zhengming.png');

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  if (!gameState || !currentRoom) {
    return null;
  }

  const isHost = currentRoom.hostId === myPlayerId;
  const authors = gameState.similarMindsAnswerAuthors ?? {};
  const voteCounts = gameState.similarMindsVoteCounts ?? {};
  const answers = gameState.similarMindsPublicAnswers ?? [];
  const winnerAnswerId = gameState.similarMindsWinnerAnswerId;
  const winnerPlayerId = gameState.similarMindsWinnerPlayerId;
  const question = gameState.similarMindsQuestion;

  const ranked = [...answers].sort((a, b) => {
    const diff = (voteCounts[b.id] ?? 0) - (voteCounts[a.id] ?? 0);
    if (diff !== 0) return diff;
    return a.text.localeCompare(b.text);
  });

  const winnerPlayer = winnerPlayerId
    ? currentRoom.players.find((p) => p.id === winnerPlayerId)
    : undefined;

  const handleNextRound = () => {
    if (!socket || !isHost) return;
    socket.emit('client:similar-minds-next-round');
  };

  const getAuthorName = (answerId: string): string => {
    const playerId = authors[answerId];
    const player = playerId ? currentRoom.players.find((p) => p.id === playerId) : undefined;
    return player?.name ?? sm.anonymousAuthor;
  };

  const closePreview = () => {
    setPreviewUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return null;
    });
  };

  const handleSaveImage = async () => {
    if (!captureRef.current || isSavingImage) return;
    setIsSavingImage(true);
    setSaveImageError(null);
    const filename = `baijia-zhengming-${Date.now()}.png`;
    try {
      const result = await captureElementImage(captureRef.current, {
        filename,
        shareTitle: sm.resultsTitle,
        shareText: t.ui.gameTypeSimilarMinds,
      });

      // Mobile share sheet already handled the file.
      if (result.shared) return;

      downloadImageBlob(result.blob, filename);
      setPreviewUrl((current) => {
        if (current) URL.revokeObjectURL(current);
        return URL.createObjectURL(result.blob);
      });
      setPreviewFilename(filename);
    } catch {
      setSaveImageError(sm.saveResultsImageFailed);
    } finally {
      setIsSavingImage(false);
    }
  };

  return (
    <div className="w-[90%] max-w-[600px] flex flex-col gap-3">
      <div
        ref={captureRef}
        className="bg-white rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 border border-white/20"
      >
        <div className="text-center mb-5">
          <h2 className="text-2xl sm:text-3xl font-bold text-teal-700">
            {sm.resultsTitle}
          </h2>
          <p className="text-xs sm:text-sm text-teal-600/80 mt-1">{t.ui.gameTypeSimilarMinds}</p>
          {gameState.similarMindsTieBroken && (
            <p className="text-sm text-amber-700 mt-2">{sm.tieBrokenNote}</p>
          )}
        </div>

        {question && (
          <div className="mb-5 p-4 rounded-xl border-2 border-teal-200 bg-gradient-to-br from-teal-50 to-cyan-50">
            <p className="text-xs sm:text-sm font-bold text-teal-700 mb-2">{sm.questionLabel}</p>
            <p className="text-base sm:text-lg text-gray-900 whitespace-pre-wrap break-words">{question}</p>
          </div>
        )}

        {winnerPlayer && (
          <p className="text-center text-lg font-bold text-amber-900 mb-4">
            {sm.winnerLabel}: {winnerPlayer.name}
          </p>
        )}

        <h3 className="text-lg font-bold text-gray-800 mb-3">{sm.rankingTitle}</h3>
        <div className="flex flex-col gap-3">
          {ranked.map((item, index) => (
            <div
              key={item.id}
              className={`p-3 sm:p-4 rounded-xl border-2 ${
                item.id === winnerAnswerId
                  ? 'border-amber-300 bg-amber-50'
                  : 'border-teal-100 bg-white'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-bold text-teal-600">#{index + 1}</span>
                  <p className="text-sm sm:text-base text-gray-800 whitespace-pre-wrap break-words mt-1">{item.text}</p>
                </div>
                <span className="text-sm font-semibold text-teal-700 shrink-0">
                  {formatString(sm.votesLabel, { count: String(voteCounts[item.id] ?? 0) })}
                </span>
              </div>
              <p className="text-xs sm:text-sm font-medium text-teal-800 mt-2">{getAuthorName(item.id)}</p>
            </div>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={handleSaveImage}
        disabled={isSavingImage}
        className="w-full bg-white text-teal-700 border-2 border-teal-200 py-3 rounded-xl font-bold disabled:opacity-60"
      >
        {isSavingImage ? sm.savingResultsImage : sm.saveResultsImage}
      </button>
      {saveImageError && (
        <p className="text-center text-sm text-red-600">{saveImageError}</p>
      )}

      {isHost ? (
        <button
          type="button"
          onClick={handleNextRound}
          className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-3 rounded-xl font-bold"
        >
          {sm.nextRound}
        </button>
      ) : (
        <p className="text-center text-sm text-gray-500">{sm.waitingForHostNextRound}</p>
      )}

      {previewUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={sm.resultsImageReady}
          onClick={closePreview}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-[90%] max-w-[600px] max-h-[90vh] flex flex-col overflow-hidden"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="p-4 border-b border-gray-100 text-center">
              <p className="font-bold text-gray-900">{sm.resultsImageReady}</p>
            </div>
            <div className="overflow-auto p-4 bg-gray-50">
              <img
                src={previewUrl}
                alt={sm.resultsTitle}
                className="w-full h-auto rounded-xl border border-gray-200 bg-white"
              />
            </div>
            <div className="p-4 flex flex-col gap-2">
              <a
                href={previewUrl}
                download={previewFilename}
                className="w-full text-center bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-3 rounded-xl font-bold"
              >
                {sm.downloadResultsImage}
              </a>
              <button
                type="button"
                onClick={closePreview}
                className="w-full bg-white text-gray-700 border-2 border-gray-200 py-3 rounded-xl font-bold"
              >
                {sm.closeResultsImagePreview}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
